"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { CategoryScene3D } from "@/components/CategoryScene3D";
import { CreatorPlatformFields } from "@/components/CreatorPlatformFields";
import { ProductCategoryFields } from "@/components/ProductCategoryFields";
import {
  formatProductCategoryPath,
  getSubcategories,
  type ProductPrimaryId,
  type ProductPrimarySelection,
} from "@/data/product-categories";
import { buildDoOrDumpFunnelInsertRow } from "@/lib/build-do-or-dump-funnel-insert-row";
import {
  DO_OR_DUMP_TIME_TO_SIGNAL_OPTIONS,
  type DoOrDumpTimeToFirstSignal,
} from "@/data/do-or-dump-funnel-fields";
import {
  clamp,
  computeDoOrDumpDerived,
  sortAcquisitions,
  type DoOrDumpAcquisition as Acquisition,
  type DoOrDumpVerdict as Verdict,
} from "@/lib/do-or-dump-compute";
import { invokeEdgeFunction } from "@/lib/edge-functions";
import { createSupabaseBrowserClient } from "@/lib/supabase/client";
import { NarrativeSkeleton } from "@/components/dashboard/NarrativeSkeleton";
import { useDoOrDumpFunnelNarrative } from "@/components/dashboard/use-do-or-dump-report-narrative";
import { reportNarrativeSnapshotFromFunnel } from "@/lib/do-or-dump-report-narrative-snapshot";
import { DO_OR_DUMP_UPSELL_BUSINESS_PUSH, DO_OR_DUMP_UPSELL_NICHE } from "@/lib/do-or-dump-upsells";
import { FUNNEL_TEXT_MAX_WORDS, truncateToMaxWords } from "@/lib/word-limit";

const ACQ_OPTIONS: { id: Acquisition; label: string }[] = [
  { id: "social", label: "Social media" },
  { id: "ads", label: "Ads" },
  { id: "word-of-mouth", label: "Word of mouth" },
  { id: "not-sure", label: "Not sure yet" },
];

function toggleAcquisition(prev: Acquisition[], id: Acquisition): Acquisition[] {
  let next: Acquisition[];
  if (id === "not-sure") {
    next = prev.includes("not-sure") ? [] : ["not-sure"];
  } else {
    const cleared = prev.filter((x) => x !== "not-sure");
    next = cleared.includes(id) ? cleared.filter((x) => x !== id) : [...cleared, id];
  }
  return next.length === 0 ? ["not-sure"] : next;
}

function formatAcquisitionsSummary(ids: Acquisition[]): string {
  return sortAcquisitions(ids)
    .map((id) => ACQ_OPTIONS.find((o) => o.id === id)?.label ?? id)
    .join(" and ");
}

function formatMoney(n: number) {
  const sign = n < 0 ? "-" : "";
  const v = Math.abs(n);
  return `${sign}$${v.toLocaleString("en-US", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;
}

/** ROI sliders: $0 … $10,000 (top tick treated as $10,000+). */
const ROI_SLIDER_MAX = 10_000;

function formatRoiSliderValue(n: number): string {
  const c = clamp(n, 0, ROI_SLIDER_MAX);
  if (c >= ROI_SLIDER_MAX) return `${formatMoney(ROI_SLIDER_MAX)}+`;
  return formatMoney(c);
}

function cashTimelineLabel(id: DoOrDumpTimeToFirstSignal): string {
  return DO_OR_DUMP_TIME_TO_SIGNAL_OPTIONS.find((o) => o.id === id)?.label ?? id;
}

function truncateBlurb(s: string, max: number) {
  const t = s.trim();
  if (!t) return "—";
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

/** One-line text fields (product copy, optional blurbs). */
const dodLineInputClass =
  "h-10 w-full rounded-xl border border-zinc-200 bg-white px-3 text-sm font-medium text-zinc-900 shadow-sm outline-none ring-violet-400 focus:ring-2 sm:h-11 sm:text-base";

const quickNumLabelClass =
  "block min-w-0 text-[10px] font-semibold uppercase tracking-wide leading-tight text-zinc-600 sm:text-xs sm:normal-case sm:tracking-normal";
const quickNumInputClass =
  "min-w-0 h-9 w-full rounded-lg border border-zinc-200 bg-white px-1.5 text-center text-sm font-semibold tabular-nums text-zinc-900 shadow-sm outline-none ring-violet-400 focus:ring-2 sm:h-10 sm:rounded-xl sm:px-2 sm:text-base";

function ProductStage({
  verdict,
  primary,
  showVerdictTint,
}: {
  verdict: Verdict;
  primary: ProductPrimarySelection;
  showVerdictTint: boolean;
}) {
  const verdictShell =
    verdict === "DO"
      ? "from-emerald-200/90 via-[#c4b5fd] to-cyan-100/90"
      : "from-rose-200/90 via-zinc-200 to-violet-100";
  const neutralShell = "from-violet-200/85 via-[#e9d5ff] to-amber-100/80";
  const shell = showVerdictTint ? verdictShell : neutralShell;

  return (
    <div
      className={`pointer-events-none relative flex min-h-[220px] flex-none items-center justify-center overflow-hidden rounded-2xl bg-gradient-to-br p-5 transition-[background] duration-500 sm:min-h-[260px] sm:rounded-3xl sm:p-8 lg:min-h-0 lg:flex-1 ${shell}`}
    >
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.45),transparent_45%)]" />

      <div
        key={primary || "unset"}
        className="pointer-events-none relative z-10 bg-transparent max-lg:[animation:none] animate-do-dump-float"
      >
        <CategoryScene3D primary={primary} tone="light" />
      </div>

      <p className="pointer-events-none absolute bottom-3 left-0 right-0 px-2 text-center text-[11px] font-medium leading-snug text-zinc-700/90 sm:bottom-4 sm:text-xs">
        Little box, big question: should you even try it?
      </p>
    </div>
  );
}

function HoursSlider({ value, onChange }: { value: number; onChange: (n: number) => void }) {
  const id = "hours-week";
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-semibold text-zinc-800">
          How many hours a week can you realistically put into this?
        </label>
        <span className="shrink-0 text-sm font-black text-violet-700">{Math.round(value)} hrs</span>
      </div>
      <div className="flex min-h-12 items-center py-1">
        <input
          id={id}
          type="range"
          min={0}
          max={40}
          step={1}
          value={clamp(value, 0, 40)}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full cursor-pointer accent-[#7c3aed]"
        />
      </div>
    </div>
  );
}

function MoneyRoiSlider({
  id,
  label,
  hint,
  value,
  onChange,
}: {
  id: string;
  label: string;
  hint?: string;
  value: number;
  onChange: (n: number) => void;
}) {
  const v = clamp(value, 0, ROI_SLIDER_MAX);
  return (
    <div className="space-y-2">
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={id} className="text-sm font-semibold text-zinc-800">
          {label}
        </label>
        <span className="shrink-0 text-sm font-black text-violet-700">{formatRoiSliderValue(v)}</span>
      </div>
      {hint ? <p className="text-xs text-zinc-600">{hint}</p> : null}
      <div className="flex min-h-12 flex-col justify-center gap-1 py-1">
        <input
          id={id}
          type="range"
          min={0}
          max={ROI_SLIDER_MAX}
          step={100}
          value={v}
          onChange={(e) => onChange(parseFloat(e.target.value))}
          className="w-full cursor-pointer accent-[#7c3aed]"
        />
        <div className="flex justify-between text-[10px] font-medium uppercase tracking-wide text-zinc-400">
          <span>$0</span>
          <span>$10,000+</span>
        </div>
      </div>
    </div>
  );
}

export default function DoOrDump() {
  const [productPrimary, setProductPrimary] = useState<ProductPrimarySelection>("");
  const [productSub, setProductSub] = useState("");
  const [productNotes, setProductNotes] = useState("");
  const [isCreator, setIsCreator] = useState(false);
  const [creatorIgFollowers, setCreatorIgFollowers] = useState(0);
  const [creatorTiktokFollowers, setCreatorTiktokFollowers] = useState(0);
  const [creatorYoutubeSubscribers, setCreatorYoutubeSubscribers] = useState(0);
  const [hoursPerWeek, setHoursPerWeek] = useState(12);
  const [followers, setFollowers] = useState(0);
  const [avgViews, setAvgViews] = useState(0);
  const [acquisitions, setAcquisitions] = useState<Acquisition[]>([]);
  const [price, setPrice] = useState(0);
  const [cost, setCost] = useState(0);
  const [ordersPerMonth, setOrdersPerMonth] = useState(0);
  const [goalMonthly, setGoalMonthly] = useState(2000);
  const [investmentBudgetUsd, setInvestmentBudgetUsd] = useState(0);
  const [timeToFirstSignal, setTimeToFirstSignal] = useState<DoOrDumpTimeToFirstSignal | null>(null);
  const [whyYou, setWhyYou] = useState("");
  const [productDifferentiation, setProductDifferentiation] = useState("");

  const [email, setEmail] = useState("");
  const [snapshotRevealed, setSnapshotRevealed] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [marketingEmailsConsent, setMarketingEmailsConsent] = useState(false);
  const [funnelSaving, setFunnelSaving] = useState(false);
  const [upsellNiche, setUpsellNiche] = useState(false);
  const [upsellBusinessPush, setUpsellBusinessPush] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [checkoutError, setCheckoutError] = useState("");
  const [paidBanner, setPaidBanner] = useState(false);

  useEffect(() => {
    const q = new URLSearchParams(window.location.search);
    if (q.get("dod_checkout") === "success") {
      setPaidBanner(true);
      q.delete("dod_checkout");
      q.delete("session_id");
      const rest = q.toString();
      window.history.replaceState({}, "", rest ? `/?${rest}` : "/");
    }
  }, []);

  useEffect(() => {
    setFollowers(Math.max(creatorIgFollowers, creatorTiktokFollowers, creatorYoutubeSubscribers));
  }, [creatorIgFollowers, creatorTiktokFollowers, creatorYoutubeSubscribers]);

  const derived = useMemo(() => {
    return computeDoOrDumpDerived({
      price,
      cost,
      ordersPerMonth,
      goalMonthly,
      hoursPerWeek,
      followers,
      acquisitions,
    });
  }, [price, cost, ordersPerMonth, goalMonthly, hoursPerWeek, followers, acquisitions]);

  const logSnapshotSubmit = () => {
    console.log("snapshot email", {
      email,
      productPrimary,
      productSub,
      productNotes,
      categoryPath: formatProductCategoryPath(productPrimary, productSub, productNotes),
      isCreator,
      creatorIgFollowers,
      creatorTiktokFollowers,
      creatorYoutubeSubscribers,
      hoursPerWeek,
      followers,
      avgViews,
      acquisitions: sortAcquisitions(acquisitions),
      price,
      cost,
      ordersPerMonth,
      goalMonthly,
      investmentBudgetUsd,
      timeToFirstSignal,
      whyYou,
      productDifferentiation,
      ...derived,
    });
  };

  const handleRevealSnapshot = async () => {
    setEmailError("");
    const trimmed = email.trim();
    if (!trimmed || !trimmed.includes("@")) {
      setEmailError("Drop a real email so we can send this.");
      return;
    }
    if (!marketingEmailsConsent) {
      setEmailError("Check the box to agree to marketing emails.");
      return;
    }
    if (productDifferentiation.trim().length < 10) {
      setEmailError("Add a sentence about what makes your product different.");
      return;
    }
    if (!productPrimary) {
      setEmailError("Pick a product category.");
      return;
    }
    const categorySubs = getSubcategories(productPrimary);
    if (categorySubs.length > 0 && !productSub.trim()) {
      setEmailError("Pick a subcategory.");
      return;
    }
    if (goalMonthly < 1) {
      setEmailError("Move the revenue slider to at least $1 per month.");
      return;
    }
    if (timeToFirstSignal === null) {
      setEmailError("Pick when you need to see cash.");
      return;
    }
    const tts = timeToFirstSignal;

    const clampFollow = (n: number): number | null => {
      if (!isCreator) return null;
      if (!Number.isFinite(n) || n < 0) return 0;
      return Math.min(Math.round(n), 999_999_999);
    };

    setFunnelSaving(true);
    try {
      const row = buildDoOrDumpFunnelInsertRow({
        email: trimmed,
        productPrimary: productPrimary as ProductPrimaryId,
        productSub,
        productNotes,
        hoursPerWeek,
        followers,
        avgViews,
        acquisitions: sortAcquisitions(acquisitions),
        price,
        cost,
        ordersPerMonth,
        goalMonthly,
        goalCustomMode: true,
        investmentBudgetUsd,
        isCreator,
        creatorIgFollowers: clampFollow(creatorIgFollowers ?? 0),
        creatorTiktokFollowers: clampFollow(creatorTiktokFollowers ?? 0),
        creatorYoutubeSubscribers: clampFollow(creatorYoutubeSubscribers ?? 0),
        timeToFirstSignal: tts,
        whyYou,
        productDifferentiation,
      });
      const sb = createSupabaseBrowserClient();
      const { error } = await sb.from("do_or_dump_funnel_events").insert(row);
      if (error && error.code !== "23505") {
        setEmailError(error.message || "Couldn't save your answers. Try again.");
        return;
      }
      try {
        await invokeEdgeFunction("funnel-invite-email", { email: trimmed.trim().toLowerCase() });
      } catch (e) {
        console.warn("[funnel] invite email skipped", e);
      }
      logSnapshotSubmit();
      setSnapshotRevealed(true);
    } catch {
      setEmailError("Couldn't save — check your connection.");
    } finally {
      setFunnelSaving(false);
    }
  };

  const handleProductPrimaryChange = useCallback((p: ProductPrimarySelection) => {
    setProductPrimary(p);

    if (!p) {
      setProductSub("");
      setProductNotes("");
      return;
    }

    if (p === "other") {
      setProductSub("");
      return;
    }

    setProductNotes("");
    setProductSub(getSubcategories(p)[0]?.id ?? "");
  }, []);

  const upsellTotal =
    (upsellNiche ? DO_OR_DUMP_UPSELL_NICHE.amountUsd : 0) +
    (upsellBusinessPush ? DO_OR_DUMP_UPSELL_BUSINESS_PUSH.amountUsd : 0);

  const handleCheckoutUpsells = async () => {
    if (upsellTotal <= 0) return;
    setCheckoutError("");
    setCheckoutLoading(true);
    try {
      const data = await invokeEdgeFunction<{ url?: string; error?: string }>("stripe-checkout", {
        email: email.trim(),
        upsellNiche,
        upsellPush: upsellBusinessPush,
        origin: window.location.origin,
      });
      if (data && typeof data === "object" && "error" in data && data.error) {
        setCheckoutError(data.error);
        return;
      }
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      setCheckoutError("No checkout URL returned.");
    } catch {
      setCheckoutError("Network error. Check your connection and try again.");
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleSkipUpsells = () => {
    console.log("skip add-ons", { email, ...derived });
  };

  const categoryPath = formatProductCategoryPath(productPrimary, productSub, productNotes);

  const funnelNarrativeSnapshot = useMemo(() => {
    if (!snapshotRevealed) return null;
    return reportNarrativeSnapshotFromFunnel({
      categoryPath,
      productPrimary,
      productSub,
      productDifferentiation,
      whyYou,
      derived,
      ordersPerMonth,
      price,
      cost,
      followers,
      avgViews,
      acquisitionsLabel: formatAcquisitionsSummary(acquisitions.length > 0 ? acquisitions : ["not-sure"]),
      goalMonthly,
      hoursPerWeek,
      investmentBudgetUsd,
      cashTimelineLabel: timeToFirstSignal ? cashTimelineLabel(timeToFirstSignal) : null,
    });
  }, [
    snapshotRevealed,
    categoryPath,
    productPrimary,
    productSub,
    productDifferentiation,
    whyYou,
    derived,
    ordersPerMonth,
    price,
    cost,
    followers,
    avgViews,
    acquisitions,
    goalMonthly,
    hoursPerWeek,
    investmentBudgetUsd,
    timeToFirstSignal,
  ]);

  const { narrative: funnelNarrative, loading: funnelNarrativeLoading } = useDoOrDumpFunnelNarrative(
    funnelNarrativeSnapshot,
  );

  return (
    <div className="flex min-h-dvh flex-col bg-[#faf8ff] text-zinc-900">
      <div className="mx-auto w-full max-w-6xl flex-1 px-3 pb-10 pt-3 sm:px-4 sm:pt-4 lg:px-8 lg:pb-12 lg:pt-6">
        {paidBanner ? (
          <div className="mb-4 flex items-start justify-between gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-950">
            <p className="font-medium">
              Payment went through — thanks. We&apos;ll follow up on the add-ons you picked at the email you used at checkout.
            </p>
            <button
              type="button"
              onClick={() => setPaidBanner(false)}
              className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-emerald-800 hover:bg-emerald-100"
            >
              Dismiss
            </button>
          </div>
        ) : null}
        <div className="mb-4 space-y-1.5 lg:hidden">
          <h1 className="text-2xl font-black leading-tight tracking-tight text-zinc-900 sm:text-3xl">Should you even try this?</h1>
          <p className="text-xs leading-relaxed text-zinc-600 sm:text-sm">
            A few honest questions + quick guesses. No spreadsheet energy.
          </p>
        </div>

        <div className="flex flex-col gap-5 lg:flex-row lg:gap-10">
          {/* Hero is decorative on mobile — full pointer-events-none stack so it cannot steal taps from the form below. */}
          <section className="order-1 flex flex-col gap-3 lg:order-1 lg:max-w-md lg:shrink-0 lg:flex-1">
            <header className="hidden space-y-1 lg:block">
              <p className="text-sm font-semibold uppercase tracking-[0.2em] text-violet-600">Do or Dump</p>
              <h1 className="text-3xl font-black tracking-tight text-zinc-900 sm:text-4xl">Should you even try this?</h1>
              <p className="text-sm text-zinc-600">
                A few plain questions and rough numbers. You get a straight Do or Dump — not a lecture.
              </p>
            </header>
            <div className="pointer-events-none">
              <ProductStage
                verdict={derived.verdict}
                primary={productPrimary}
                showVerdictTint={snapshotRevealed}
              />
            </div>
          </section>

          <section className="order-2 relative flex min-h-0 flex-col gap-5 max-lg:z-[999] max-lg:isolate lg:order-2 lg:flex-[1.15]">
            {/* Large screens: fixed-height card + inner scroll. Small screens: normal flow — page scroll only. */}
            <div className="relative mx-auto w-full lg:h-[clamp(22rem,min(76dvh,38.75rem),38.75rem)] lg:min-h-0">
              {/* Front: questions + email strip */}
              <div
                className={`flex flex-col rounded-2xl border border-zinc-100 bg-white/95 shadow-lg shadow-violet-200/30 sm:rounded-3xl lg:shadow-xl lg:shadow-violet-200/40 motion-safe:transition-opacity motion-safe:duration-500 motion-safe:ease-in-out ${
                  snapshotRevealed
                    ? "hidden lg:absolute lg:inset-0 lg:z-0 lg:min-h-0 lg:overflow-hidden lg:opacity-0 lg:pointer-events-none"
                    : "relative z-[2] lg:absolute lg:inset-0 lg:min-h-0 lg:overflow-hidden lg:opacity-100"
                }`}
                aria-hidden={snapshotRevealed}
              >
                <div
                  className="relative z-10 min-h-0 p-4 pb-8 sm:p-6 md:p-8 lg:flex-[1_1_0] lg:min-h-0 lg:overflow-y-auto lg:overscroll-y-contain lg:touch-pan-y"
                  style={{ WebkitOverflowScrolling: "touch" }}
                >
                      <div className="flex flex-col gap-10 sm:gap-12">
                        <div>
                            <h2 className="text-xl font-black tracking-tight text-zinc-900 sm:text-2xl">Your Product</h2>
                            <p className="mt-1 text-sm text-zinc-600">Start with what you&apos;re selling.</p>
                            <div className="mt-5 flex flex-col gap-6">
                              <ProductCategoryFields
                                variant="light"
                                primary={productPrimary}
                                sub={productSub}
                                notes={productNotes}
                                onPrimary={handleProductPrimaryChange}
                                onSub={setProductSub}
                                onNotes={setProductNotes}
                              />
                              <div className="space-y-2">
                                <label htmlFor="product-diff" className="block text-sm font-semibold text-zinc-800">
                                  What makes your product different? <span className="text-rose-600">*</span>
                                </label>
                                <input
                                  id="product-diff"
                                  type="text"
                                  value={productDifferentiation}
                                  onChange={(e) => {
                                    setProductDifferentiation(truncateToMaxWords(e.target.value, FUNNEL_TEXT_MAX_WORDS));
                                    setEmailError("");
                                  }}
                                  placeholder="Say it straight — quality, price, niche, business model, speed, etc."
                                  className={dodLineInputClass}
                                />
                              </div>
                            </div>
                        </div>

                        <div className="border-t border-zinc-100 pt-10 sm:pt-12">
                            <h2 className="text-xl font-black tracking-tight text-zinc-900 sm:text-2xl">
                              Return on Investment
                            </h2>
                            <p className="mt-1 text-sm text-zinc-600">Money goals, runway, time — rough is fine.</p>
                            <div className="mt-5 flex flex-col gap-6">
                              <MoneyRoiSlider
                                id="roi-goal-monthly"
                                label="How much do you want this to make per month?"
                                value={goalMonthly}
                                onChange={setGoalMonthly}
                              />
                              <MoneyRoiSlider
                                id="roi-investment"
                                label="How much can you invest in the business?"
                                hint="Rough total you could put in — inventory, ads, tools, samples. $0 is fine if you're not sure yet."
                                value={investmentBudgetUsd}
                                onChange={setInvestmentBudgetUsd}
                              />
                              <div className="space-y-3">
                                <p className="text-sm font-semibold text-zinc-800">When do you need to see cash?</p>
                                <p className="text-xs text-zinc-600">Rough guess is fine.</p>
                                <div className="flex flex-wrap gap-2">
                                  {DO_OR_DUMP_TIME_TO_SIGNAL_OPTIONS.map((o) => (
                                    <button
                                      key={o.id}
                                      type="button"
                                      onClick={() => setTimeToFirstSignal(o.id)}
                                      className={`min-h-11 rounded-full px-4 py-2 text-sm font-bold transition active:scale-[0.98] ${
                                        timeToFirstSignal !== null && timeToFirstSignal === o.id
                                          ? "bg-violet-600 text-white shadow-md"
                                          : "border border-zinc-200 bg-white text-zinc-700 hover:bg-violet-50"
                                      }`}
                                    >
                                      {o.label}
                                    </button>
                                  ))}
                                </div>
                              </div>
                              <div className="space-y-2 rounded-xl border border-zinc-100 bg-zinc-50/60 p-3 sm:space-y-3 sm:p-4">
                                <p className="text-sm font-semibold text-zinc-800">
                                  Quick numbers <span className="font-normal text-zinc-500">(estimate — 0 is ok)</span>
                                </p>
                                <div className="grid grid-cols-3 gap-2 sm:gap-3">
                                  <div className="min-w-0 space-y-1">
                                    <label htmlFor="price" className={quickNumLabelClass}>
                                      Price
                                    </label>
                                    <input
                                      id="price"
                                      type="number"
                                      inputMode="numeric"
                                      min={0}
                                      max={2000}
                                      step={1}
                                      value={price}
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === "") {
                                          setPrice(0);
                                          return;
                                        }
                                        const v = parseFloat(raw);
                                        if (Number.isNaN(v)) setPrice(0);
                                        else setPrice(clamp(v, 0, 2000));
                                      }}
                                      className={quickNumInputClass}
                                    />
                                  </div>
                                  <div className="min-w-0 space-y-1">
                                    <label htmlFor="cost" className={quickNumLabelClass}>
                                      <span className="sm:hidden">Cost</span>
                                      <span className="hidden sm:inline">Cost to make</span>
                                    </label>
                                    <input
                                      id="cost"
                                      type="number"
                                      inputMode="numeric"
                                      min={0}
                                      max={2000}
                                      step={1}
                                      value={cost}
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === "") {
                                          setCost(0);
                                          return;
                                        }
                                        const v = parseFloat(raw);
                                        if (Number.isNaN(v)) setCost(0);
                                        else setCost(clamp(v, 0, 2000));
                                      }}
                                      className={quickNumInputClass}
                                    />
                                  </div>
                                  <div className="min-w-0 space-y-1">
                                    <label htmlFor="orders" className={quickNumLabelClass}>
                                      <span className="sm:hidden">Orders/mo</span>
                                      <span className="hidden sm:inline">Monthly orders</span>
                                    </label>
                                    <input
                                      id="orders"
                                      type="number"
                                      inputMode="numeric"
                                      min={0}
                                      max={50_000}
                                      step={1}
                                      value={ordersPerMonth}
                                      onChange={(e) => {
                                        const raw = e.target.value;
                                        if (raw === "") {
                                          setOrdersPerMonth(0);
                                          return;
                                        }
                                        const v = parseFloat(raw);
                                        if (Number.isNaN(v)) setOrdersPerMonth(0);
                                        else setOrdersPerMonth(clamp(v, 0, 50_000));
                                      }}
                                      className={quickNumInputClass}
                                    />
                                  </div>
                                </div>
                              </div>
                              <HoursSlider value={hoursPerWeek} onChange={setHoursPerWeek} />
                            </div>
                        </div>

                        <div className="border-t border-zinc-100 pt-10 sm:pt-12">
                            <h2 className="text-xl font-black tracking-tight text-zinc-900 sm:text-2xl">Your Market</h2>
                            <p className="mt-1 text-sm text-zinc-600">Attention now — and how people might find you.</p>
                            <div className="mt-5 flex flex-col gap-6">
                              <div className="space-y-3 rounded-xl border border-violet-100 bg-violet-50/40 p-4">
                                <p className="text-sm font-semibold text-zinc-800">Do people already pay attention to you?</p>
                                <CreatorPlatformFields
                                  variant="light"
                                  isCreator={isCreator}
                                  onIsCreator={setIsCreator}
                                  creatorIgFollowers={creatorIgFollowers}
                                  creatorTiktokFollowers={creatorTiktokFollowers}
                                  creatorYoutubeSubscribers={creatorYoutubeSubscribers}
                                  onCreatorIgFollowers={setCreatorIgFollowers}
                                  onCreatorTiktokFollowers={setCreatorTiktokFollowers}
                                  onCreatorYoutubeSubscribers={setCreatorYoutubeSubscribers}
                                  avgViewsPerPost={avgViews}
                                  onAvgViewsPerPost={setAvgViews}
                                />
                              </div>
                              <div className="space-y-3">
                                <p className="text-sm font-semibold text-zinc-800">How do you think people will find you?</p>
                                <p className="text-xs text-zinc-600">
                                  Pick any that apply — social, ads, word of mouth, or not sure yet.
                                </p>
                                <div className="flex flex-col gap-2">
                                  {ACQ_OPTIONS.map((o) => {
                                    const checked = acquisitions.includes(o.id);
                                    return (
                                      <label
                                        key={o.id}
                                        className="flex min-h-12 cursor-pointer gap-3 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50/80 has-[:checked]:ring-1 has-[:checked]:ring-violet-300"
                                      >
                                        <input
                                          type="checkbox"
                                          checked={checked}
                                          onChange={() => setAcquisitions((prev) => toggleAcquisition(prev, o.id))}
                                          className="mt-0.5 size-5 shrink-0 accent-violet-600"
                                        />
                                        <span className="text-sm font-medium leading-snug text-zinc-800">{o.label}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            </div>
                        </div>

                        <div className="border-t border-zinc-100 pt-10 sm:pt-12">
                            <h2 className="text-xl font-black tracking-tight text-zinc-900 sm:text-2xl">
                              A few final questions
                            </h2>
                            <div className="mt-5 flex flex-col gap-6">
                              <div className="space-y-2">
                                <label htmlFor="why-you" className="block text-sm font-semibold text-zinc-800">
                                  Why you?{" "}
                                  <span className="font-normal text-zinc-500">(optional)</span>
                                </label>
                                <input
                                  id="why-you"
                                  type="text"
                                  value={whyYou}
                                  onChange={(e) =>
                                    setWhyYou(truncateToMaxWords(e.target.value, FUNNEL_TEXT_MAX_WORDS))
                                  }
                                  placeholder="Skills, audience, story"
                                  className={dodLineInputClass}
                                />
                              </div>
                              <div className="space-y-2">
                                <label htmlFor="dod-email" className="block text-sm font-semibold text-zinc-800">
                                  Email <span className="text-rose-600">*</span>
                                </label>
                                <input
                                  id="dod-email"
                                  type="email"
                                  inputMode="email"
                                  autoComplete="email"
                                  placeholder="your@email.com"
                                  value={email}
                                  onChange={(e) => {
                                    setEmail(e.target.value);
                                    setEmailError("");
                                  }}
                                  className={dodLineInputClass}
                                />
                              </div>
                              <label className="flex min-h-12 cursor-pointer gap-3 rounded-xl border border-zinc-200 bg-white p-3 shadow-sm has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50/80 has-[:checked]:ring-1 has-[:checked]:ring-violet-300">
                                <input
                                  type="checkbox"
                                  checked={marketingEmailsConsent}
                                  onChange={(e) => {
                                    setMarketingEmailsConsent(e.target.checked);
                                    setEmailError("");
                                  }}
                                  className="mt-0.5 size-5 shrink-0 accent-violet-600"
                                />
                                <span className="text-sm font-medium leading-snug text-zinc-800">
                                  I agree to receive marketing emails from crshm.{" "}
                                  <span className="text-rose-600">*</span>
                                </span>
                              </label>
                              {emailError ? <p className="text-sm text-rose-600">{emailError}</p> : null}
                              <div className="pb-[max(0.5rem,env(safe-area-inset-bottom))] pt-2">
                                <button
                                  type="button"
                                  onClick={() => void handleRevealSnapshot()}
                                  disabled={funnelSaving}
                                  className="min-h-11 w-full rounded-xl bg-violet-600 px-4 py-3 text-sm font-bold text-white shadow-md transition hover:bg-violet-700 active:scale-[0.98] disabled:cursor-wait disabled:opacity-80 sm:w-auto sm:min-w-[11rem]"
                                >
                                  {funnelSaving ? "Saving…" : "Show my snapshot"}
                                </button>
                              </div>
                            </div>
                        </div>
                      </div>
                </div>
              </div>

              {/* Back: snapshot + verdict + upsells */}
              <div
                className={`flex flex-col rounded-2xl border border-violet-200 bg-white shadow-lg sm:rounded-3xl lg:shadow-xl motion-safe:transition-opacity motion-safe:duration-500 motion-safe:ease-in-out ${
                  snapshotRevealed
                    ? "relative z-[2] lg:absolute lg:inset-0 lg:z-[2] lg:min-h-0 lg:overflow-hidden lg:opacity-100"
                    : "hidden lg:absolute lg:inset-0 lg:z-0 lg:min-h-0 lg:overflow-hidden lg:opacity-0 lg:pointer-events-none"
                }`}
                aria-hidden={!snapshotRevealed}
              >
                <div
                  className="p-4 sm:p-6 md:p-8 lg:min-h-0 lg:flex-[1_1_0] lg:overflow-y-scroll lg:overscroll-y-contain lg:touch-pan-y"
                  style={{ WebkitOverflowScrolling: "touch" }}
                >
                      <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">Your snapshot</p>
                      <p className="mt-1 text-xs text-zinc-500">Heads up: {email.trim() || "your inbox"}</p>

                      <div className="mt-4 grid gap-3 sm:grid-cols-2">
                        <ResultLine label="Money per sale (after cost)" value={formatMoney(derived.profitPerItem)} />
                        <ResultLine label="Rough monthly profit at your guess" value={formatMoney(derived.monthlyProfitGuess)} />
                        <ResultLine
                          label="Orders to hit $2,000 / month"
                          value={derived.unitsFor2k == null ? "Impossible" : `~${derived.unitsFor2k.toLocaleString("en-US")}`}
                        />
                        <ResultLine
                          label={`To hit ${formatRoiSliderValue(goalMonthly)}/mo, you need`}
                          value={
                            derived.unitsForGoal == null
                              ? "Impossible"
                              : `~${derived.unitsForGoal.toLocaleString("en-US")} sales / month`
                          }
                        />
                        <ResultLine
                          label="Investment you said you could put in"
                          value={formatRoiSliderValue(investmentBudgetUsd)}
                        />
                        <ResultLine
                          label="How people might find you"
                          value={formatAcquisitionsSummary(acquisitions.length > 0 ? acquisitions : ["not-sure"])}
                        />
                        <ResultLine
                          label="When you need to see cash"
                          value={timeToFirstSignal ? cashTimelineLabel(timeToFirstSignal) : "—"}
                        />
                        <ResultLine
                          label="Typical views per post or video"
                          value={avgViews.toLocaleString("en-US")}
                        />
                        <ResultLine label="What makes your product different" value={truncateBlurb(productDifferentiation, 160)} />
                        {whyYou.trim() ? <ResultLine label="Why you" value={truncateBlurb(whyYou, 160)} /> : null}
                      </div>
                      <p className="mt-3 text-xs text-zinc-500">
                        Selling: <span className="font-medium text-zinc-700">{categoryPath}</span> · setup read{" "}
                        <span className="font-semibold text-violet-700">{derived.momentum}/100</span> (time + attention +
                        how people find you)
                      </p>

                      <div className="mt-6 flex flex-col items-center gap-3 rounded-2xl border border-zinc-100 bg-gradient-to-b from-white to-violet-50/80 p-5 text-center">
                        <div
                          className={`inline-flex min-h-16 min-w-[7rem] items-center justify-center rounded-2xl px-5 py-3 text-3xl font-black tracking-tight ring-4 sm:min-h-0 sm:min-w-[8rem] sm:text-4xl ${derived.badgeClass}`}
                        >
                          {derived.verdict}
                        </div>
                        <p className="max-w-md text-sm font-medium leading-snug text-zinc-700 sm:text-base">{derived.verdictLine}</p>
                      </div>

                      <div className="mt-6 space-y-5">
                        {funnelNarrativeLoading ? <NarrativeSkeleton /> : null}

                        {funnelNarrative && !funnelNarrativeLoading ? (
                          <>
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">Overview</p>
                              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-zinc-800">
                                {funnelNarrative.overview}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">Inputs vs model</p>
                              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-zinc-800">
                                {funnelNarrative.inputsVsModel}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">Competitor corner</p>
                              <ul className="mt-2 space-y-3 text-sm leading-relaxed text-zinc-800">
                                <li>
                                  <p className="text-[10px] font-bold uppercase tracking-wide text-violet-600">High end</p>
                                  <p className="mt-1 whitespace-pre-line">{funnelNarrative.competitorHighEnd}</p>
                                </li>
                                <li>
                                  <p className="text-[10px] font-bold uppercase tracking-wide text-violet-600">Middle</p>
                                  <p className="mt-1 whitespace-pre-line">{funnelNarrative.competitorMiddle}</p>
                                </li>
                                <li>
                                  <p className="text-[10px] font-bold uppercase tracking-wide text-violet-600">Peer / new entrant</p>
                                  <p className="mt-1 whitespace-pre-line">{funnelNarrative.competitorPeer}</p>
                                </li>
                              </ul>
                            </div>
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">Market pulse</p>
                              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-zinc-800">
                                {funnelNarrative.marketPulse}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">Differentiation</p>
                              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-zinc-800">
                                {funnelNarrative.differentiation}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">Socials &amp; acquisition</p>
                              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-zinc-800">
                                {funnelNarrative.socialsAcquisition}
                              </p>
                            </div>
                            <div>
                              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">Why you</p>
                              <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-zinc-800">
                                {funnelNarrative.whyYou}
                              </p>
                            </div>
                            <div className="rounded-xl border border-violet-100 bg-violet-50/60 px-3 py-3">
                              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-violet-700">One high impact tweak</p>
                              <p className="mt-2 text-sm font-bold leading-relaxed text-zinc-900">
                                Based on what you put in, here&apos;s what I&apos;d question first:{" "}
                                {funnelNarrative.highImpactTweak}
                              </p>
                            </div>
                          </>
                        ) : null}
                      </div>

                      <div className="mt-8 space-y-3">
                        <p className="text-sm font-bold text-zinc-900">Want more on top of this?</p>
                        <p className="text-xs text-zinc-600">Pick one, both, or neither — prices are up front.</p>

                        <label className="flex min-h-14 cursor-pointer gap-3 rounded-xl border border-zinc-200 bg-zinc-50/80 p-3 has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50">
                          <input
                            type="checkbox"
                            checked={upsellNiche}
                            onChange={(e) => setUpsellNiche(e.target.checked)}
                            className="mt-1 size-5 shrink-0 accent-violet-600"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-zinc-900">{DO_OR_DUMP_UPSELL_NICHE.label}</span>
                            <span className="mt-1 block text-xs leading-relaxed text-zinc-600">{DO_OR_DUMP_UPSELL_NICHE.detail}</span>
                            <span className="mt-1 block text-xs font-bold text-zinc-900">
                              Turnaround: {DO_OR_DUMP_UPSELL_NICHE.turnaround}
                            </span>
                            <span className="mt-1 block text-sm font-black text-violet-700">
                              ${DO_OR_DUMP_UPSELL_NICHE.amountUsd.toFixed(2)}
                            </span>
                          </span>
                        </label>

                        <label className="flex min-h-14 cursor-pointer gap-3 rounded-xl border border-zinc-200 bg-zinc-50/80 p-3 has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50">
                          <input
                            type="checkbox"
                            checked={upsellBusinessPush}
                            onChange={(e) => setUpsellBusinessPush(e.target.checked)}
                            className="mt-1 size-5 shrink-0 accent-violet-600"
                          />
                          <span className="min-w-0 flex-1">
                            <span className="block font-semibold text-zinc-900">{DO_OR_DUMP_UPSELL_BUSINESS_PUSH.label}</span>
                            <span className="mt-1 block text-xs leading-relaxed text-zinc-600">
                              {DO_OR_DUMP_UPSELL_BUSINESS_PUSH.detail}
                            </span>
                            <span className="mt-1 block text-xs font-bold text-zinc-900">
                              Turnaround: {DO_OR_DUMP_UPSELL_BUSINESS_PUSH.turnaround}
                            </span>
                            <span className="mt-1 block text-sm font-black text-violet-700">
                              ${DO_OR_DUMP_UPSELL_BUSINESS_PUSH.amountUsd.toFixed(2)}
                            </span>
                          </span>
                        </label>

                        <div className="flex items-center justify-between rounded-lg bg-zinc-100 px-3 py-2 text-sm">
                          <span className="font-medium text-zinc-600">Selected total</span>
                          <span className="font-black text-zinc-900">${upsellTotal.toFixed(2)}</span>
                        </div>
                      </div>

                      <div className="mt-8 space-y-2 rounded-xl border border-violet-100 bg-violet-50/90 p-4 pb-[max(1rem,env(safe-area-inset-bottom))] sm:p-5">
                        <button
                          type="button"
                          onClick={() => void handleCheckoutUpsells()}
                          className="min-h-12 w-full rounded-xl bg-violet-600 px-4 py-3 text-sm font-bold text-white shadow-md transition hover:bg-violet-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50"
                          disabled={upsellTotal <= 0 || checkoutLoading}
                        >
                          {checkoutLoading
                            ? "Opening checkout…"
                            : upsellTotal > 0
                              ? `Continue to checkout — $${upsellTotal.toFixed(2)}`
                              : "Pick an add-on above to continue"}
                        </button>
                        {checkoutError ? <p className="text-center text-sm text-rose-600">{checkoutError}</p> : null}
                        <button
                          type="button"
                          onClick={handleSkipUpsells}
                          className="w-full py-2 text-center text-xs font-semibold text-zinc-600 underline decoration-zinc-400 underline-offset-2 hover:text-zinc-900"
                        >
                          Skip add-ons for now
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSnapshotRevealed(false);
                            setUpsellNiche(false);
                            setUpsellBusinessPush(false);
                            setEmailError("");
                          }}
                          className="w-full py-1 text-center text-xs text-zinc-500 hover:text-zinc-800"
                        >
                          Change my answers
                        </button>
                      </div>
                </div>
              </div>
            </div>
          </section>
        </div>
      </div>

    </div>
  );
}

function ResultLine({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-zinc-100 bg-zinc-50/80 px-3 py-2.5">
      <p className="text-[10px] font-semibold uppercase tracking-wide text-zinc-500">{label}</p>
      <p className="mt-0.5 text-base font-bold text-zinc-900 sm:text-lg">{value}</p>
    </div>
  );
}
